import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Avatar,
  Button,
  Empty,
  Form,
  Input,
  message,
  Modal,
  Popconfirm,
  Segmented,
  Select,
  Space,
  Spin,
  Switch,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import {
  PlusOutlined,
  ReloadOutlined,
  DeleteOutlined,
  KeyOutlined,
  CheckCircleOutlined,
  WarningOutlined,
  InstagramOutlined,
  GlobalOutlined,
  LinkOutlined,
  ApiOutlined,
} from '@ant-design/icons';
import { liveChatApi, show422Error, ValidationError } from '../../utils/api';
import { PANEL_PRIMARY_BTN_CLASS } from '../../constants/ui';
import { formatChatTime } from './liveChatMeta';

const INSTAGRAM_GRADIENT = 'linear-gradient(45deg, #f09433, #e6683c, #dc2743, #cc2366, #bc1888)';

const DELIVERY_OPTIONS = [
  { value: 'webhook', label: <span className="custom-font">وب‌هوک (لحظه‌ای)</span> },
  { value: 'polling', label: <span className="custom-font">دریافت دوره‌ای</span> },
];

const PROVIDER_OPTIONS = [
  { value: 'meta', label: <span className="custom-font">مستقیم با Meta</span> },
  { value: 'zernio', label: <span className="custom-font">از طریق Zernio</span> },
];

/** API هر راه اتصال؛ ردیف‌ها با provider مشخص می‌شوند */
const PROVIDER_API = {
  meta: {
    list: () => liveChatApi.getInstagramAccounts(),
    create: (payload) => liveChatApi.createInstagramAccount(payload),
    update: (id, payload) => liveChatApi.updateInstagramAccount(id, payload),
    remove: (id) => liveChatApi.removeInstagramAccount(id),
    check: (id) => liveChatApi.checkInstagramAccount(id),
  },
  zernio: {
    list: () => liveChatApi.getZernioInstagramAccounts(),
    create: (payload) => liveChatApi.createZernioInstagramAccount(payload),
    update: (id, payload) => liveChatApi.updateZernioInstagramAccount(id, payload),
    remove: (id) => liveChatApi.removeZernioInstagramAccount(id),
    check: (id) => liveChatApi.checkZernioInstagramAccount(id),
  },
};

const apiFor = (account) => PROVIDER_API[account?.provider === 'zernio' ? 'zernio' : 'meta'];

/** مراحل ساخت اپ Meta و گرفتن توکن (برای حساب خود شرکت بررسی اپ لازم نیست) */
const META_SETUP_STEPS = [
  'حساب اینستاگرام باید «حرفه‌ای» (Business یا Creator) باشد.',
  'در developers.facebook.com یک اپ از نوع Business بسازید و محصول «Instagram» را اضافه کنید (API setup with Instagram login).',
  'در بخش «Generate access tokens» حساب اینستاگرام را اضافه کنید، وارد شوید و توکن تولیدشده را اینجا وارد کنید.',
  '«Instagram app secret» همان صفحه را هم وارد کنید تا امضای وب‌هوک بررسی شود.',
  'در اپ اینستاگرام: Settings › Messages and calls › Connected tools › «Allow access to messages» را روشن کنید.',
  'برای وب‌هوک: پس از اتصال، آدرس و توکن تأیید نمایش‌داده‌شده را در «Configure webhooks» اپ Meta وارد و فیلد messages را فعال کنید.',
];

const ZERNIO_SETUP_STEPS = [
  'در zernio.com حساب بسازید، افزونه Inbox (پیام‌ها) را فعال کنید و از داشبورد Zernio یک کلید API بسازید.',
  'کلید را وارد کنید و «دریافت حساب‌ها» را بزنید. اگر حساب اینستاگرام هنوز در Zernio نیست، «اتصال حساب جدید» را بزنید و در صفحه اینستاگرام وارد شوید.',
  'در اپ اینستاگرام: Settings › Messages and calls › Connected tools › «Allow access to messages» را روشن کنید.',
  'وب‌هوک پیام‌ها خودکار در Zernio ثبت می‌شود؛ تنظیم دستی لازم نیست.',
];

/** وضعیت اتصال یک حساب */
const AccountStatus = ({ account }) => {
  const st = account.status || {};
  if (!account.is_active) return <Tag className="custom-font">غیرفعال</Tag>;
  if (st.ok === false) {
    return (
      <Tooltip title={st.error}>
        <Tag color="red" icon={<WarningOutlined />} className="custom-font">خطای اتصال</Tag>
      </Tooltip>
    );
  }
  if (account.provider === 'zernio' && st.account_active === false) {
    return <Tag color="orange" icon={<WarningOutlined />} className="custom-font">حساب در Zernio قطع است</Tag>;
  }
  if (account.delivery === 'webhook') {
    const registered = account.provider === 'zernio' ? account.webhook_registered : st.webhook_subscribed;
    if (!registered) return <Tag color="orange" className="custom-font">وب‌هوک ثبت نشده</Tag>;
    return (
      <Tooltip title={account.last_inbound_at || account.provider === 'zernio' ? null : 'هنوز پیامی از وب‌هوک نرسیده؛ آدرس و توکن تأیید را در اپ Meta بررسی کنید.'}>
        <Tag color="green" icon={<CheckCircleOutlined />} className="custom-font">وب‌هوک فعال</Tag>
      </Tooltip>
    );
  }
  if (account.last_poll_error) {
    return (
      <Tooltip title={account.last_poll_error}>
        <Tag color="orange" icon={<WarningOutlined />} className="custom-font">خطای دریافت دوره‌ای</Tag>
      </Tooltip>
    );
  }
  return (
    <Tooltip title={account.last_polled_at ? `آخرین دریافت: ${formatChatTime(account.last_polled_at)}` : 'هنوز دریافتی انجام نشده'}>
      <Tag color={account.last_polled_at ? 'green' : 'default'} icon={account.last_polled_at ? <CheckCircleOutlined /> : null} className="custom-font">
        دریافت دوره‌ای
      </Tag>
    </Tooltip>
  );
};

/** آدرس وب‌هوک و توکن تأیید برای ثبت در داشبورد Meta (فقط اتصال مستقیم) */
const WebhookInfo = ({ account }) => (
  <div className="w-full rounded-md bg-gray-50 dark:bg-white/5 p-2 text-xs custom-font flex flex-col gap-1">
    <div className="text-gray-500">در اپ Meta › Instagram › API setup › Configure webhooks وارد کنید و فیلد messages را Subscribe کنید:</div>
    <div className="flex items-center gap-2 flex-wrap">
      <span className="text-gray-500">Callback URL:</span>
      <Typography.Text style={{ direction: 'ltr' }} copyable className="text-xs">{account.webhook_url}</Typography.Text>
    </div>
    <div className="flex items-center gap-2 flex-wrap">
      <span className="text-gray-500">Verify token:</span>
      <Typography.Text style={{ direction: 'ltr' }} copyable className="text-xs">{account.verify_token}</Typography.Text>
    </div>
    {!account.has_app_secret && (
      <div className="text-amber-600">App secret وارد نشده؛ امضای درخواست‌های وب‌هوک بررسی نمی‌شود.</div>
    )}
  </div>
);

/**
 * مدیریت حساب‌های اینستاگرام شرکت (دایرکت)، مستقیم با Meta یا از طریق Zernio.
 * توکن، app secret و کلید API فقط نوشتنی‌اند و دوباره نمایش داده نمی‌شوند.
 */
const InstagramAccountsCard = ({ proxyAvailable = false }) => {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState({ open: false, editing: null });
  const [provider, setProvider] = useState('meta');
  const [saving, setSaving] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [zernio, setZernio] = useState({ loading: false, loaded: false, profiles: [], accounts: [], connecting: false });
  const [form] = Form.useForm();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [meta, viaZernio] = await Promise.all([
        PROVIDER_API.meta.list(),
        PROVIDER_API.zernio.list().catch(() => ({ data: [] })),
      ]);
      setAccounts([...(meta?.data ?? []), ...(viaZernio?.data ?? [])]);
    } catch (error) {
      message.error(error?.message || 'خطا در دریافت حساب‌های اینستاگرام');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const replaceAccount = (account) => setAccounts((prev) => prev.map((a) => (a.id === account.id ? account : a)));

  const openModal = (account = null) => {
    setModal({ open: true, editing: account });
    setProvider(account?.provider === 'zernio' ? 'zernio' : 'meta');
    setZernio({ loading: false, loaded: false, profiles: [], accounts: [], connecting: false });
    form.resetFields();
    form.setFieldsValue(
      account
        ? {
            name: account.name,
            delivery: account.delivery,
            use_proxy: Boolean(account.use_proxy),
            human_agent_tag: Boolean(account.human_agent_tag),
          }
        : { delivery: undefined, use_proxy: proxyAvailable, human_agent_tag: false },
    );
  };

  /** فهرست پروفایل‌ها و حساب‌های اینستاگرام Zernio با کلید فعلی فرم */
  const lookupZernio = async () => {
    setZernio((z) => ({ ...z, loading: true }));
    try {
      const { api_key: apiKey, use_proxy: useProxy } = form.getFieldsValue(['api_key', 'use_proxy']);
      const res = await liveChatApi.zernioLookup({ api_key: apiKey?.trim() || null, use_proxy: Boolean(useProxy) });
      const data = res?.data ?? {};
      const profiles = data.profiles ?? [];
      setZernio((z) => ({ ...z, loading: false, loaded: true, profiles, accounts: data.accounts ?? [] }));
      if (!form.getFieldValue('zernio_profile_id')) {
        form.setFieldsValue({ zernio_profile_id: (profiles.find((p) => p.is_default) || profiles[0])?.id });
      }
    } catch (error) {
      setZernio((z) => ({ ...z, loading: false }));
      if (error instanceof ValidationError) show422Error(error, form);
      else message.error(error?.message || 'خطا در دریافت حساب‌های Zernio');
    }
  };

  /** OAuth اتصال حساب جدید در Zernio در پنجره جدا؛ پس از بازگشت «دریافت حساب‌ها» دوباره زده می‌شود */
  const connectNewInZernio = async () => {
    setZernio((z) => ({ ...z, connecting: true }));
    try {
      const { api_key: apiKey, use_proxy: useProxy, zernio_profile_id: profileId } = form.getFieldsValue(['api_key', 'use_proxy', 'zernio_profile_id']);
      const res = await liveChatApi.zernioConnectUrl({
        api_key: apiKey?.trim() || null,
        use_proxy: Boolean(useProxy),
        profile_id: profileId,
        redirect_url: `${window.location.origin}${window.location.pathname}`,
      });
      const url = res?.data?.auth_url;
      if (url) {
        window.open(url, '_blank', 'noopener');
        message.info('پس از ورود به اینستاگرام در پنجره باز شده، «دریافت حساب‌ها» را دوباره بزنید.', 6);
      }
    } catch (error) {
      if (error instanceof ValidationError) show422Error(error, form);
      else message.error(error?.message || 'خطا در ساخت لینک اتصال');
    } finally {
      setZernio((z) => ({ ...z, connecting: false }));
    }
  };

  const submit = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);
      const payload = {
        name: values.name || undefined,
        delivery: values.delivery || undefined,
        use_proxy: Boolean(values.use_proxy),
        human_agent_tag: Boolean(values.human_agent_tag),
      };
      if (provider === 'zernio') {
        if (values.api_key) payload.api_key = values.api_key.trim();
        if (!modal.editing) payload.zernio_account_id = values.zernio_account_id;
      } else {
        if (values.access_token) payload.access_token = values.access_token.trim();
        if (values.app_secret) payload.app_secret = values.app_secret.trim();
      }
      const api = PROVIDER_API[provider];
      const res = modal.editing ? await api.update(modal.editing.id, payload) : await api.create(payload);
      const account = res?.data?.account;
      if (account) {
        if (modal.editing) replaceAccount(account);
        else setAccounts((prev) => [...prev, account]);
      }
      if (res?.data?.warning) message.warning(res.data.warning, 6);
      else message.success(modal.editing ? 'حساب بروزرسانی شد' : 'حساب اینستاگرام متصل شد');
      setModal({ open: false, editing: null });
    } catch (error) {
      if (error?.errorFields) return;
      if (error instanceof ValidationError) show422Error(error, form);
      else message.error(error?.message || 'خطا در ذخیره حساب');
    } finally {
      setSaving(false);
    }
  };

  const quickUpdate = async (account, payload, successText) => {
    setBusyId(account.id);
    try {
      const res = await apiFor(account).update(account.id, payload);
      if (res?.data?.account) replaceAccount(res.data.account);
      if (res?.data?.warning) message.warning(res.data.warning, 6);
      else if (successText) message.success(successText);
    } catch (error) {
      message.error(error?.message || 'خطا در بروزرسانی حساب');
    } finally {
      setBusyId(null);
    }
  };

  const check = async (account) => {
    setBusyId(account.id);
    try {
      const res = await apiFor(account).check(account.id);
      if (res?.data) replaceAccount(res.data);
    } catch (error) {
      message.error(error?.message || 'خطا در بررسی اتصال');
    } finally {
      setBusyId(null);
    }
  };

  const remove = async (account) => {
    setBusyId(account.id);
    try {
      await apiFor(account).remove(account.id);
      setAccounts((prev) => prev.filter((a) => a.id !== account.id));
      message.success('حساب اینستاگرام جدا شد');
    } catch (error) {
      message.error(error?.message || 'خطا در حذف حساب');
    } finally {
      setBusyId(null);
    }
  };

  const isZernio = provider === 'zernio';
  const editing = modal.editing;

  return (
    <div className="livechat-panel p-4 mb-6">
      <div className="flex items-center justify-between gap-2 flex-wrap mb-3">
        <div>
          <div className="font-bold text-gray-800 dark:text-gray-100 custom-font flex items-center gap-2">
            <InstagramOutlined style={{ color: '#e1306c' }} /> دایرکت اینستاگرام
          </div>
          <div className="text-xs text-gray-500 custom-font mt-1">
            پیام‌های دایرکت حساب حرفه‌ای شرکت (متن، عکس، ویدیو، صوت و پاسخ به استوری) در صندوق گفتگو نمایش داده می‌شود و پاسخ اپراتور از طرف همان حساب ارسال می‌شود.
            اتصال مستقیم با اپ Meta یا از طریق سرویس Zernio ممکن است. طبق قوانین اینستاگرام فقط تا ۲۴ ساعت پس از آخرین پیام مشتری می‌توان پاسخ داد.
          </div>
        </div>
        <Space>
          <Button icon={<ReloadOutlined />} onClick={load} loading={loading} style={{ fontFamily: 'CustomFont' }}>
            بروزرسانی
          </Button>
          <Button type="primary" icon={<PlusOutlined />} onClick={() => openModal()} className={PANEL_PRIMARY_BTN_CLASS} style={{ fontFamily: 'CustomFont' }}>
            افزودن حساب
          </Button>
        </Space>
      </div>

      <Spin spinning={loading}>
        {accounts.length === 0 ? (
          <Empty className="py-6" description={<span className="custom-font">حساب اینستاگرامی متصل نشده است</span>} />
        ) : (
          <div className="flex flex-col gap-2">
            {accounts.map((account) => (
              <div key={account.id} className="rounded-lg border border-primary-100 dark:border-white/10 bg-white dark:bg-white/5 p-3 flex items-center gap-3 flex-wrap">
                <Avatar style={{ background: INSTAGRAM_GRADIENT }} icon={<InstagramOutlined />} />
                <div className="min-w-0 flex-1">
                  <div className="font-medium custom-font text-gray-800 dark:text-gray-100 flex items-center gap-2 flex-wrap">
                    {account.name}
                    {account.username && (
                      <Typography.Text className="text-xs text-gray-500" style={{ direction: 'ltr' }} copyable={{ text: `@${account.username}` }}>
                        @{account.username}
                      </Typography.Text>
                    )}
                    {account.provider === 'zernio' && (
                      <Tag icon={<ApiOutlined />} color="purple" className="custom-font">Zernio</Tag>
                    )}
                  </div>
                  <div className="text-xs text-gray-500 custom-font flex items-center gap-2 flex-wrap mt-1">
                    <AccountStatus account={account} />
                    {account.use_proxy && (
                      <Tag icon={<GlobalOutlined />} color="blue" className="custom-font">
                        پراکسی
                      </Tag>
                    )}
                    {account.token_refresh_error && (
                      <Tooltip title={account.token_refresh_error}>
                        <Tag color="red" icon={<WarningOutlined />} className="custom-font">تمدید توکن ناموفق</Tag>
                      </Tooltip>
                    )}
                    {account.token_expires_at && <span>اعتبار توکن تا {new Date(account.token_expires_at).toLocaleDateString('fa-IR')}</span>}
                    {account.last_inbound_at && <span>آخرین پیام: {formatChatTime(account.last_inbound_at)}</span>}
                  </div>
                </div>
                <Space wrap>
                  <Segmented
                    size="small"
                    value={account.delivery}
                    options={DELIVERY_OPTIONS}
                    disabled={busyId === account.id}
                    onChange={(v) => quickUpdate(account, { delivery: v }, 'روش دریافت تغییر کرد')}
                  />
                  <Tooltip title={proxyAvailable ? 'استفاده از پراکسی' : 'پراکسی در تب «عمومی» تعریف نشده است'}>
                    <Switch
                      size="small"
                      checkedChildren={<GlobalOutlined />}
                      unCheckedChildren={<GlobalOutlined />}
                      checked={Boolean(account.use_proxy)}
                      disabled={!proxyAvailable && !account.use_proxy}
                      loading={busyId === account.id}
                      onChange={(v) => quickUpdate(account, { use_proxy: v }, v ? 'پراکسی فعال شد' : 'پراکسی غیرفعال شد')}
                    />
                  </Tooltip>
                  <Tooltip title={account.is_active ? 'فعال' : 'غیرفعال'}>
                    <Switch size="small" checked={account.is_active} loading={busyId === account.id} onChange={(v) => quickUpdate(account, { is_active: v })} />
                  </Tooltip>
                  <Tooltip title="بررسی اتصال">
                    <Button size="small" icon={<ReloadOutlined />} onClick={() => check(account)} loading={busyId === account.id} />
                  </Tooltip>
                  <Tooltip title={account.provider === 'zernio' ? 'ویرایش' : 'ویرایش / توکن جدید'}>
                    <Button size="small" icon={<KeyOutlined />} onClick={() => openModal(account)} />
                  </Tooltip>
                  <Popconfirm
                    title="این حساب جدا شود؟"
                    description={
                      account.provider === 'zernio'
                        ? 'وب‌هوک در Zernio حذف می‌شود؛ حساب در Zernio متصل می‌ماند و گفتگوهای قبلی در صندوق می‌مانند.'
                        : 'اشتراک وب‌هوک حساب لغو می‌شود؛ گفتگوهای قبلی در صندوق می‌مانند.'
                    }
                    okText="بله"
                    cancelText="خیر"
                    onConfirm={() => remove(account)}
                  >
                    <Button size="small" danger icon={<DeleteOutlined />} />
                  </Popconfirm>
                </Space>
                {account.provider !== 'zernio' && account.delivery === 'webhook' && account.is_active && <WebhookInfo account={account} />}
              </div>
            ))}
          </div>
        )}
      </Spin>

      <Modal
        title={editing ? `ویرایش ${editing.name}` : 'افزودن حساب اینستاگرام'}
        open={modal.open}
        onCancel={() => setModal({ open: false, editing: null })}
        onOk={submit}
        confirmLoading={saving}
        destroyOnHidden
        okText={editing ? 'ذخیره' : 'اتصال'}
        cancelText="انصراف"
        okButtonProps={{ className: PANEL_PRIMARY_BTN_CLASS }}
      >
        {!editing && (
          <>
            <Segmented block options={PROVIDER_OPTIONS} value={provider} onChange={setProvider} className="mb-3" />
            <Alert
              type="info"
              showIcon
              className="custom-font mb-4"
              title={isZernio ? 'راه‌اندازی با Zernio' : 'راه‌اندازی با اپ Meta'}
              description={
                <ol className="list-decimal pr-4 m-0 text-xs leading-6">
                  {(isZernio ? ZERNIO_SETUP_STEPS : META_SETUP_STEPS).map((step) => (
                    <li key={step}>{step}</li>
                  ))}
                </ol>
              }
            />
          </>
        )}
        <Form form={form} layout="vertical" className="custom-font">
          {isZernio ? (
            <>
              <Form.Item
                name="api_key"
                label="کلید API زرنیو (Zernio)"
                extra={
                  editing
                    ? 'خالی = بدون تغییر.'
                    : 'خالی = کلید اتصال Zernio قبلی همین شرکت یا کلید سراسری سرور (LIVECHAT_ZERNIO_API_KEY). به‌صورت رمزنگاری‌شده ذخیره می‌شود.'
                }
              >
                <Input.Password dir="ltr" placeholder="sk_..." autoComplete="new-password" maxLength={300} />
              </Form.Item>
              {!editing && (
                <>
                  <Button icon={<ReloadOutlined />} onClick={lookupZernio} loading={zernio.loading} className="mb-3" style={{ fontFamily: 'CustomFont' }}>
                    دریافت حساب‌ها
                  </Button>
                  {zernio.loaded && (
                    <>
                      <Form.Item
                        name="zernio_account_id"
                        label="حساب اینستاگرام در Zernio"
                        rules={[{ required: true, message: 'حساب را انتخاب کنید' }]}
                        extra={zernio.accounts.length === 0 ? 'حساب اینستاگرامی در Zernio نیست؛ از دکمه زیر یکی متصل کنید.' : null}
                      >
                        <Select
                          placeholder="انتخاب حساب"
                          options={zernio.accounts.map((a) => ({
                            value: a.id,
                            disabled: a.connected,
                            label: `${a.display_name || a.username || a.id}${a.username ? ` (@${a.username})` : ''}${a.profile_name ? ` · ${a.profile_name}` : ''}${a.connected ? ' — متصل' : ''}${a.is_active ? '' : ' — قطع'}`,
                          }))}
                        />
                      </Form.Item>
                      <div className="livechat-panel p-3 mb-3">
                        <div className="text-xs text-gray-500 mb-2">حساب در فهرست نیست؟ آن را در Zernio متصل کنید (ورود به اینستاگرام در پنجره جدید):</div>
                        <div className="flex gap-2 flex-wrap items-start">
                          <Form.Item name="zernio_profile_id" className="mb-0 flex-1 min-w-[160px]">
                            <Select placeholder="پروفایل Zernio" options={zernio.profiles.map((p) => ({ value: p.id, label: p.name }))} />
                          </Form.Item>
                          <Button icon={<LinkOutlined />} onClick={connectNewInZernio} loading={zernio.connecting} disabled={zernio.profiles.length === 0} style={{ fontFamily: 'CustomFont' }}>
                            اتصال حساب جدید
                          </Button>
                        </div>
                      </div>
                    </>
                  )}
                </>
              )}
            </>
          ) : (
            <>
              <Form.Item
                name="access_token"
                label="توکن دسترسی (Access token)"
                rules={editing ? [] : [{ required: true, message: 'توکن دسترسی را وارد کنید' }]}
                extra={
                  editing
                    ? 'خالی = بدون تغییر. توکن جدید باید متعلق به همین حساب باشد. توکن هر هفته خودکار تمدید می‌شود.'
                    : 'توکن بلندمدت (۶۰ روزه) که خودکار تمدید می‌شود؛ به‌صورت رمزنگاری‌شده ذخیره و دوباره نمایش داده نمی‌شود.'
                }
              >
                <Input.Password dir="ltr" placeholder="IGAA..." autoComplete="new-password" maxLength={1000} />
              </Form.Item>
              <Form.Item
                name="app_secret"
                label="Instagram app secret (پیشنهادی)"
                extra={
                  editing?.has_app_secret
                    ? 'قبلاً ثبت شده؛ برای تغییر مقدار جدید وارد کنید.'
                    : 'برای بررسی امضای وب‌هوک (X-Hub-Signature-256)؛ بدون آن هر کسی که آدرس وب‌هوک را بداند می‌تواند پیام جعلی بفرستد.'
                }
              >
                <Input.Password dir="ltr" autoComplete="new-password" maxLength={200} />
              </Form.Item>
            </>
          )}
          <Form.Item name="name" label="نام نمایشی (اختیاری)">
            <Input maxLength={150} placeholder="پیش‌فرض: نام حساب در اینستاگرام" />
          </Form.Item>
          <Form.Item
            name="delivery"
            label="روش دریافت پیام"
            extra={
              isZernio
                ? 'وب‌هوک لحظه‌ای است و خودکار در Zernio ثبت می‌شود، ولی Zernio باید به آدرس HTTPS عمومی این سرور دسترسی داشته باشد. دریافت دوره‌ای همه‌جا کار می‌کند.'
                : 'وب‌هوک لحظه‌ای است ولی به آدرس HTTPS عمومی که Meta به آن دسترسی دارد نیاز دارد. دریافت دوره‌ای همه‌جا کار می‌کند (هر چند ثانیه).'
            }
          >
            <Segmented options={DELIVERY_OPTIONS} />
          </Form.Item>
          <Form.Item
            name="use_proxy"
            valuePropName="checked"
            label="استفاده از پراکسی"
            extra={
              proxyAvailable
                ? `همه درخواست‌ها به ${isZernio ? 'Zernio' : 'اینستاگرام'} و دانلود فایل‌ها از پراکسی تنظیمات گفتگوی آنلاین عبور می‌کند. روی سرورهای داخل ایران لازم است.`
                : 'پراکسی تعریف نشده است؛ روی سرورهای داخل ایران ابتدا در تب «عمومی» آدرس پراکسی را وارد و ذخیره کنید.'
            }
          >
            <Switch disabled={!proxyAvailable} />
          </Form.Item>
          <Form.Item
            name="human_agent_tag"
            valuePropName="checked"
            label="برچسب Human Agent (پاسخ تا ۷ روز)"
            extra="فقط اگر دسترسی Human Agent برای اپ (یا اپ Zernio) تأیید شده باشد روشن کنید؛ در غیر این صورت پاسخ‌های بعد از ۲۴ ساعت رد می‌شوند."
          >
            <Switch />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default InstagramAccountsCard;
